/**
 * Path Converter - Bidirectional conversion between tree node keys and entity paths
 *
 * Формат путей: @audience[ID].actors[ID].field[index]
 * Оптимизация: если выбраны ВСЕ дочерние элементы, сохраняется только родительский путь
 */

import type { AudienceTreeNode } from './types';
import {
  collectAllCheckableKeys,
  isFullyChecked,
  markAsProcessed,
  findNodeByKey,
} from '../../shared/treeHelpers';

/**
 * Конвертирует ключ узла дерева в путь формата @audience[ID].actors[ID].field[index]
 */
export function treeNodeIdToPath(nodeKey: string, allNodes: AudienceTreeNode[]): string | null {
  const node = findNodeByKey(nodeKey, allNodes) as AudienceTreeNode | null;
  if (!node) return null;

  switch (node.nodeType) {
    case 'audience':
      // Целая audience: @audience[ID]
      return `@audience[${node.audienceId}]`;

    case 'actor':
      // Целый actor: @audience[ID].actors[ID]
      return `@audience[${node.audienceId}].actors[${node.actorId}]`;

    case 'subcollection':
      // Целая subcollection: @audience[ID].actors[ID].fears
      if (node.actorId && node.subcollectionName) {
        return `@audience[${node.audienceId}].actors[${node.actorId}].${node.subcollectionName}`;
      }
      return null;

    case 'field':
      // Поле: @audience[ID].field или @audience[ID].actors[ID].field
      if (node.actorId) {
        return `@audience[${node.audienceId}].actors[${node.actorId}].${node.fieldName}`;
      } else {
        return `@audience[${node.audienceId}].${node.fieldName}`;
      }

    case 'arrayItem':
      // Элемент массива: @audience[ID].actors[ID].field[index]
      return `@audience[${node.audienceId}].actors[${node.actorId}].${node.fieldName}[${node.arrayIndex}]`;

    default:
      return null;
  }
}

/**
 * Конвертирует массив выбранных ключей узлов в массив путей
 * С ОПТИМИЗАЦИЕЙ: если выбраны все дети - возвращает путь родителя
 */
export function treeNodeIdsToPaths(checkedIds: string[], allNodes: AudienceTreeNode[]): string[] {
  const checkedSet = new Set(checkedIds);
  const processedNodes = new Set<string>();
  const paths: string[] = [];

  // Проверяем целые audiences
  for (const audienceNode of allNodes) {
    if (processedNodes.has(audienceNode.key as string)) continue;

    if (isFullyChecked(audienceNode, checkedSet)) {
      const path = treeNodeIdToPath(audienceNode.key as string, allNodes);
      if (path) {
        paths.push(path);
        markAsProcessed(audienceNode, processedNodes);
      }
      continue;
    }

    // Проверяем целых actors внутри audience
    const actorsFolder = audienceNode.children?.find(
      (c) => c.nodeType === 'subcollection' && c.key === `aud-${audienceNode.audienceId}-actors`
    );

    if (actorsFolder?.children) {
      for (const actorNode of actorsFolder.children) {
        if (processedNodes.has(actorNode.key as string)) continue;

        if (isFullyChecked(actorNode, checkedSet)) {
          const path = treeNodeIdToPath(actorNode.key as string, allNodes);
          if (path) {
            paths.push(path);
            markAsProcessed(actorNode, processedNodes);
          }
          continue;
        }

        // Проверяем subcollections внутри actor (fears, coreJobs, etc.)
        if (actorNode.children) {
          for (const subcollectionNode of actorNode.children) {
            if (subcollectionNode.nodeType === 'subcollection') {
              if (processedNodes.has(subcollectionNode.key as string)) continue;

              if (isFullyChecked(subcollectionNode, checkedSet)) {
                const path = treeNodeIdToPath(subcollectionNode.key as string, allNodes);
                if (path) {
                  paths.push(path);
                  markAsProcessed(subcollectionNode, processedNodes);
                }
              }
            }
          }
        }
      }
    }
  }

  // Добавляем оставшиеся индивидуальные пути (не оптимизированные)
  for (const id of checkedIds) {
    if (!processedNodes.has(id)) {
      const path = treeNodeIdToPath(id, allNodes);
      if (path) {
        paths.push(path);
      }
    }
  }

  return paths;
}

/**
 * Парсит путь формата @audience[ID].actors[ID].field[index]
 */
function parsePath(path: string): {
  audienceId?: string;
  actorId?: string;
  fieldName?: string;
  arrayIndex?: number;
  firebaseItemId?: string;
  subcollectionName?: string;
  type: 'audience' | 'actor' | 'field' | 'arrayItem' | 'subcollection';
} | null {
  // Целая audience: @audience[ID]
  const audienceMatch = path.match(/^@audience\[([^\]]+)\]$/);
  if (audienceMatch) {
    return { audienceId: audienceMatch[1], type: 'audience' };
  }

  // Целый actor: @audience[ID].actors[ID]
  const actorMatch = path.match(/^@audience\[([^\]]+)\]\.actors\[([^\]]+)\]$/);
  if (actorMatch) {
    return { audienceId: actorMatch[1], actorId: actorMatch[2], type: 'actor' };
  }

  // Элемент массива: @audience[ID].actors[ID].field[indexOrFirebaseId] with optional .suffix
  // Supports both index format: fears[0] and Firebase ID format: fears[NY2dMsB0wY5PGREDQF65]
  // Also strips trailing .description or other field suffixes
  const arrayItemMatch = path.match(/^@audience\[([^\]]+)\]\.actors\[([^\]]+)\]\.([^[]+)\[([^\]]+)\](?:\.\w+)?$/);
  if (arrayItemMatch) {
    const indexOrId = arrayItemMatch[4];
    const isNumericIndex = /^\d+$/.test(indexOrId);
    return {
      audienceId: arrayItemMatch[1],
      actorId: arrayItemMatch[2],
      fieldName: arrayItemMatch[3],
      arrayIndex: isNumericIndex ? parseInt(indexOrId, 10) : undefined,
      firebaseItemId: isNumericIndex ? undefined : indexOrId,
      type: 'arrayItem',
    };
  }

  // Целая subcollection: @audience[ID].actors[ID].fears (без индекса)
  // Also matches actor field paths like: @audience[ID].actors[ID].description
  const subcollectionMatch = path.match(/^@audience\[([^\]]+)\]\.actors\[([^\]]+)\]\.([^.[]+)$/);
  if (subcollectionMatch) {
    const subcollectionName = subcollectionMatch[3];
    // Проверяем, что это известная subcollection (а не обычное поле)
    const knownSubcollections = ['coreJobs', 'contexts', 'fears', 'bigJobs', 'subtasks', 'competitors', 'habits'];
    if (knownSubcollections.includes(subcollectionName)) {
      return {
        audienceId: subcollectionMatch[1],
        actorId: subcollectionMatch[2],
        subcollectionName,
        type: 'subcollection',
      };
    } else {
      // Обычное поле actor
      return {
        audienceId: subcollectionMatch[1],
        actorId: subcollectionMatch[2],
        fieldName: subcollectionMatch[3],
        type: 'field',
      };
    }
  }

  // Поле audience: @audience[ID].field
  const audienceFieldMatch = path.match(/^@audience\[([^\]]+)\]\.([^[]+)$/);
  if (audienceFieldMatch) {
    return {
      audienceId: audienceFieldMatch[1],
      fieldName: audienceFieldMatch[2],
      type: 'field',
    };
  }

  return null;
}

/**
 * Конвертирует один путь в массив ключей узлов дерева (для гидратации)
 */
function pathToTreeNodeIds_single(path: string, allNodes: AudienceTreeNode[]): string[] {
  const parsed = parsePath(path);
  if (!parsed) {
    console.debug('[pathConverter] parsePath returned null for:', path);
    return [];
  }
  console.debug('[pathConverter] parsePath result:', { path, parsed, nodesCount: allNodes.length });

  // Целая audience - возвращаем все checkable ключи внутри
  if (parsed.type === 'audience') {
    const audienceNode = findNodeByKey(`aud-${parsed.audienceId}`, allNodes);
    if (audienceNode) {
      return collectAllCheckableKeys(audienceNode);
    }
    return [];
  }

  // Целый actor - возвращаем все checkable ключи внутри
  if (parsed.type === 'actor') {
    const actorNode = findNodeByKey(`act-${parsed.actorId}`, allNodes);
    if (actorNode) {
      return collectAllCheckableKeys(actorNode);
    }
    return [];
  }

  // Целая subcollection - возвращаем все checkable ключи внутри
  if (parsed.type === 'subcollection') {
    const subcollectionNode = findNodeByKey(`act-${parsed.actorId}-${parsed.subcollectionName}`, allNodes);
    if (subcollectionNode) {
      return collectAllCheckableKeys(subcollectionNode);
    }
    return [];
  }

  // Поле или элемент массива - возвращаем один ключ
  if (parsed.type === 'field') {
    if (parsed.actorId) {
      return [`act-${parsed.actorId}-${parsed.fieldName}`];
    } else {
      return [`aud-${parsed.audienceId}-${parsed.fieldName}`];
    }
  }

  if (parsed.type === 'arrayItem') {
    if (parsed.arrayIndex != null) {
      // Numeric index path: fears[0] → act-{actorId}-fears-0
      return [`act-${parsed.actorId}-${parsed.fieldName}-${parsed.arrayIndex}`];
    }
    if (parsed.firebaseItemId) {
      // Firebase ID path: fears[NY2dMsB0...] → find matching tree node by itemFirebaseId
      const lookupKey = `act-${parsed.actorId}-${parsed.fieldName}`;
      console.debug('[pathConverter] Firebase ID lookup:', { lookupKey, firebaseItemId: parsed.firebaseItemId });
      const subcollectionNode = findNodeByKey(lookupKey, allNodes) as AudienceTreeNode | null;
      console.debug('[pathConverter] subcollectionNode found:', !!subcollectionNode, subcollectionNode?.children?.length);
      if (subcollectionNode?.children) {
        const matchingChild = subcollectionNode.children.find(
          (c) => (c as AudienceTreeNode).itemFirebaseId === parsed.firebaseItemId
        );
        if (matchingChild) return [matchingChild.key as string];
      }
      // Fallback: try to find across all actor's subcollection nodes
      const actorNode = findNodeByKey(`act-${parsed.actorId}`, allNodes) as AudienceTreeNode | null;
      if (actorNode?.children) {
        for (const child of actorNode.children) {
          if ((child as AudienceTreeNode).subcollectionName === parsed.fieldName && child.children) {
            const match = child.children.find(
              (c) => (c as AudienceTreeNode).itemFirebaseId === parsed.firebaseItemId
            );
            if (match) return [match.key as string];
          }
        }
      }
      return [];
    }
    return [];
  }

  return [];
}

/**
 * Умная дедупликация путей - удаляет child пути ТОЛЬКО если выбраны ВСЕ дочерние элементы
 *
 * В отличие от простой дедупликации по префиксу, эта функция проверяет через isFullyChecked,
 * действительно ли все дочерние узлы выбраны перед оптимизацией.
 *
 * Примеры:
 * - Все дети выбраны: ["@audience[ID]", "@audience[ID].name", ...все поля] -> ["@audience[ID]"]
 * - Частичный выбор: ["@audience[ID].actors[ID].name"] -> ["@audience[ID].actors[ID].name"]
 * - rc-tree первый клик (неполные данные): ["@audience[ID]", "@audience[ID].name"] -> ["@audience[ID].name"]
 *
 * @param paths - Массив путей для дедупликации
 * @param allNodes - Все узлы дерева (для проверки isFullyChecked)
 * @returns Оптимизированный массив путей
 */
export function deduplicatePaths(paths: string[], allNodes: AudienceTreeNode[]): string[] {
  if (paths.length === 0) return [];

  const checkedKeys = new Set<string>();

  // Собираем все node keys из путей
  for (const path of paths) {
    const nodeKeys = pathToTreeNodeIds_single(path, allNodes);
    nodeKeys.forEach((key) => checkedKeys.add(key));
  }

  // Используем ту же логику что и в treeNodeIdsToPaths
  const processedNodes = new Set<string>();
  const result: string[] = [];

  // Проверяем целые audiences
  for (const audienceNode of allNodes) {
    if (processedNodes.has(audienceNode.key as string)) continue;

    if (isFullyChecked(audienceNode, checkedKeys)) {
      const path = treeNodeIdToPath(audienceNode.key as string, allNodes);
      if (path) {
        result.push(path);
        markAsProcessed(audienceNode, processedNodes);
      }
      continue;
    }

    // Проверяем целых actors внутри audience
    const actorsFolder = audienceNode.children?.find(
      (c) => c.nodeType === 'subcollection' && c.key === `aud-${audienceNode.audienceId}-actors`
    );

    if (actorsFolder?.children) {
      for (const actorNode of actorsFolder.children) {
        if (processedNodes.has(actorNode.key as string)) continue;

        if (isFullyChecked(actorNode, checkedKeys)) {
          const path = treeNodeIdToPath(actorNode.key as string, allNodes);
          if (path) {
            result.push(path);
            markAsProcessed(actorNode, processedNodes);
          }
          continue;
        }

        // Проверяем subcollections внутри actor
        if (actorNode.children) {
          for (const subcollectionNode of actorNode.children) {
            if (subcollectionNode.nodeType === 'subcollection') {
              if (processedNodes.has(subcollectionNode.key as string)) continue;

              if (isFullyChecked(subcollectionNode, checkedKeys)) {
                const path = treeNodeIdToPath(subcollectionNode.key as string, allNodes);
                if (path) {
                  result.push(path);
                  markAsProcessed(subcollectionNode, processedNodes);
                }
              }
            }
          }
        }
      }
    }
  }

  // Добавляем оставшиеся индивидуальные пути (не оптимизированные)
  for (const path of paths) {
    // Проверяем, не был ли этот путь уже обработан
    const parsed = parsePath(path);
    if (!parsed) continue;

    let nodeKey: string;
    if (parsed.type === 'audience') {
      nodeKey = `aud-${parsed.audienceId}`;
    } else if (parsed.type === 'actor') {
      nodeKey = `act-${parsed.actorId}`;
    } else if (parsed.type === 'subcollection') {
      nodeKey = `act-${parsed.actorId}-${parsed.subcollectionName}`;
    } else if (parsed.type === 'field') {
      nodeKey = parsed.actorId
        ? `act-${parsed.actorId}-${parsed.fieldName}`
        : `aud-${parsed.audienceId}-${parsed.fieldName}`;
    } else if (parsed.type === 'arrayItem') {
      nodeKey = `act-${parsed.actorId}-${parsed.fieldName}-${parsed.arrayIndex}`;
    } else {
      continue;
    }

    if (!processedNodes.has(nodeKey)) {
      result.push(path);
    }
  }

  return result;
}

/**
 * Конвертирует массив путей в массив ключей узлов дерева (для гидратации)
 *
 * ВАЖНО: Эта функция используется для ГИДРАТАЦИИ (загрузка сохраненных данных в состояние дерева).
 * Дедупликация НЕ должна применяться здесь - она должна происходить только в handleCheck
 * при сохранении данных НАРУЖУ к родительскому компоненту.
 *
 * @param paths - Массив путей (например, ["@audience[ID1]", "@audience[ID2]"])
 * @param allNodes - Все узлы дерева
 * @returns Массив ключей узлов (раскрытый, включая всех детей)
 */
export function pathsToTreeNodeIds(paths: string[], allNodes: AudienceTreeNode[]): string[] {
  const keys = new Set<string>();

  for (const path of paths) {
    const nodeKeys = pathToTreeNodeIds_single(path, allNodes);
    nodeKeys.forEach((key) => keys.add(key));
  }

  return Array.from(keys);
}
